import {Trans} from '@lingui/macro';
import _, {compact, throttle} from 'lodash';
import React, {useMemo} from 'react';

import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {
  VirtualTableColumnCellsEnum,
  virtualTableRowCheckboxWrapperWidth,
  virtualTableRowOffset,
  VirtualTableSortableColumnDirectionsEnum,
} from '../../components/common/table/helpers/tableHelpers';
import {
  SelectedColumnSort,
  SortableTableColumn,
  TableColumn,
} from '../../components/common/table/tableColumn';
import {getTagChildrenIds, SearchTagSortsEnum} from '../../helpers/models/tagHelpers';
import {SearchTagModel} from '../../models/api/searchTagModel';
import {TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {ResourceMap} from '../../store/appTypes';
import {
  renderSettingsResourcesTableColumnName,
  SettingsResourcesTableColumnsEnum,
} from '../common/settingsResourcesCommon';

export const settingsTagsListSortableColumns: ReadonlyArray<SortableTableColumn> = [
  SettingsResourcesTableColumnsEnum.PATH,
  SettingsResourcesTableColumnsEnum.NAME,
  SettingsResourcesTableColumnsEnum.LAST_USED,
  SettingsResourcesTableColumnsEnum.DATE_CREATED,
].map((c) => ({
  id: c,
  directions: [
    VirtualTableSortableColumnDirectionsEnum.ASCENDING,
    VirtualTableSortableColumnDirectionsEnum.DESCENDING,
  ],
}));

export function useSettingsTagsListColumns(
  isInArchivedView: boolean,
  isInPersonalNamespace: boolean,
): ReadonlyArray<TableColumn<SettingsResourcesTableColumnsEnum>> {
  const localize = useLocalize();

  const result = useMemo(
    () =>
      compact([
        isInArchivedView
          ? {
              id: SettingsResourcesTableColumnsEnum.NAME,
              type: SettingsResourcesTableColumnsEnum.NAME,
              title: renderSettingsResourcesTableColumnName(SettingsResourcesTableColumnsEnum.NAME, localize),
              cellType: VirtualTableColumnCellsEnum.TEXT,
              settings: {
                sticky: {isSticky: true, stickyOffset: virtualTableRowCheckboxWrapperWidth},
                cellWidth: {
                  shouldFillRemainingWidth: true,
                  minWidth: 330 - virtualTableRowOffset,
                },
              },
            }
          : {
              id: SettingsResourcesTableColumnsEnum.PATH,
              type: SettingsResourcesTableColumnsEnum.PATH,
              title: renderSettingsResourcesTableColumnName(SettingsResourcesTableColumnsEnum.PATH, localize),
              cellType: VirtualTableColumnCellsEnum.TEXT,
              settings: {
                sticky: {isSticky: true, stickyOffset: virtualTableRowCheckboxWrapperWidth},
                cellWidth: {
                  shouldFillRemainingWidth: true,
                  minWidth: 330 - virtualTableRowOffset,
                },
              },
            },
        !isInPersonalNamespace && {
          id: SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
          type: SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
          title: renderSettingsResourcesTableColumnName(
            SettingsResourcesTableColumnsEnum.AVAILABLE_IN,
            localize,
          ),
          cellType: VirtualTableColumnCellsEnum.TEXT,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
          },
        },
        {
          id: SettingsResourcesTableColumnsEnum.LAST_USED,
          type: SettingsResourcesTableColumnsEnum.LAST_USED,
          title: renderSettingsResourcesTableColumnName(
            SettingsResourcesTableColumnsEnum.LAST_USED,
            localize,
          ),
          cellType: VirtualTableColumnCellsEnum.DATE,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
            tooltip: <Trans id="settingstags-nolastusedat">No records before April 2021</Trans>,
          },
        },
        {
          id: SettingsResourcesTableColumnsEnum.DATE_CREATED,
          type: SettingsResourcesTableColumnsEnum.DATE_CREATED,
          title: renderSettingsResourcesTableColumnName(
            SettingsResourcesTableColumnsEnum.DATE_CREATED,
            localize,
          ),
          cellType: VirtualTableColumnCellsEnum.DATE,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
          },
        },
        isInArchivedView && {
          id: SettingsResourcesTableColumnsEnum.ARCHIVED_DELETE,
          type: SettingsResourcesTableColumnsEnum.ARCHIVED_DELETE,
          title: '',
          cellType: VirtualTableColumnCellsEnum.TEXT,
          settings: {
            cellWidth: {
              width: 140 - virtualTableRowOffset,
            },
          },
        },
      ]),
    [isInArchivedView, isInPersonalNamespace, localize],
  );

  return result;
}

export function computeSelectedColumnSortFromTagSort(
  tagSort: SearchTagSortsEnum | undefined,
): SelectedColumnSort | undefined {
  if (!tagSort) {
    return undefined;
  }

  switch (tagSort) {
    case SearchTagSortsEnum.NAME_ASC: {
      return {
        id: SettingsResourcesTableColumnsEnum.NAME,
        direction: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      };
    }
    case SearchTagSortsEnum.NAME_DESC: {
      return {
        id: SettingsResourcesTableColumnsEnum.NAME,
        direction: VirtualTableSortableColumnDirectionsEnum.DESCENDING,
      };
    }
    case SearchTagSortsEnum.PATH_ASC: {
      return {
        id: SettingsResourcesTableColumnsEnum.PATH,
        direction: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      };
    }
    case SearchTagSortsEnum.PATH_DESC: {
      return {
        id: SettingsResourcesTableColumnsEnum.PATH,
        direction: VirtualTableSortableColumnDirectionsEnum.DESCENDING,
      };
    }
    case SearchTagSortsEnum.LAST_USED_AT_ASC: {
      return {
        id: SettingsResourcesTableColumnsEnum.LAST_USED,
        direction: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      };
    }
    case SearchTagSortsEnum.LAST_USED_AT_DESC: {
      return {
        id: SettingsResourcesTableColumnsEnum.LAST_USED,
        direction: VirtualTableSortableColumnDirectionsEnum.DESCENDING,
      };
    }
    case SearchTagSortsEnum.CREATED_AT_ASC: {
      return {
        id: SettingsResourcesTableColumnsEnum.DATE_CREATED,
        direction: VirtualTableSortableColumnDirectionsEnum.ASCENDING,
      };
    }
    case SearchTagSortsEnum.CREATED_AT_DESC: {
      return {
        id: SettingsResourcesTableColumnsEnum.DATE_CREATED,
        direction: VirtualTableSortableColumnDirectionsEnum.DESCENDING,
      };
    }

    default: {
      return undefined;
    }
  }
}

export function computeTagSortFromSelectedColumnSort(
  sort: SelectedColumnSort | undefined,
): SearchTagSortsEnum | undefined {
  if (!sort) {
    return undefined;
  }

  const {id, direction} = sort;

  switch (id) {
    case SettingsResourcesTableColumnsEnum.PATH: {
      return direction === VirtualTableSortableColumnDirectionsEnum.ASCENDING
        ? SearchTagSortsEnum.PATH_ASC
        : SearchTagSortsEnum.PATH_DESC;
    }
    case SettingsResourcesTableColumnsEnum.NAME: {
      return direction === VirtualTableSortableColumnDirectionsEnum.ASCENDING
        ? SearchTagSortsEnum.NAME_ASC
        : SearchTagSortsEnum.NAME_DESC;
    }
    case SettingsResourcesTableColumnsEnum.LAST_USED: {
      return direction === VirtualTableSortableColumnDirectionsEnum.ASCENDING
        ? SearchTagSortsEnum.LAST_USED_AT_ASC
        : SearchTagSortsEnum.LAST_USED_AT_DESC;
    }
    case SettingsResourcesTableColumnsEnum.DATE_CREATED: {
      return direction === VirtualTableSortableColumnDirectionsEnum.ASCENDING
        ? SearchTagSortsEnum.CREATED_AT_ASC
        : SearchTagSortsEnum.CREATED_AT_DESC;
    }
    default:
      return undefined;
  }
}

export const findParentsToFetchInSearchTags = (
  searchTags: ReadonlyArray<SearchTagModel>,
  tagsIdMap: ResourceMap<TagModel>,
  isInArchivedView: boolean,
) =>
  _(searchTags)
    .flatten()
    .filter((t) => Boolean(isInArchivedView ? t.archivedAt : !t.archivedAt))
    .filter((t) => getTagChildrenIds(t, isInArchivedView).some((tId) => !tagsIdMap[tId]))
    .map((t) => t.id)
    .compact()
    .uniq()
    .value();

export const trackSettingsTagsSearchEvent = throttle((tagsUrl: SettingsTagsUrl) => {
  trackInterfaceEvent(InterfaceEventsEnum.SETTINGS_TAG_SEARCH, {
    settings_subpage: tagsUrl.tabPage,
    namespace: tagsUrl.namespace,
  });
}, 5 * 1000);
